// AssigneePicker — smart user selector for action assignment.
//
// Features:
//   • Alpha-sorted by surname always
//   • Pin/favourite users (per-persona, localStorage) — pinned users appear at top
//   • Free-type search: contains filter, dynamically filters as you type
//   • Flat list shows first 10, "Show X more" to expand
//   • When searching: shows all matches, no 10-limit
//   • Excludes the current persona (can't assign to yourself)
//
// Charlotte / Devin wiring notes:
//   • pinnedUserIds are stored in localStorage keyed by `actions-pinned-${personaId}`.
//     In live mode you may want to persist pins to the API instead — swap the
//     localStorage read/write calls in usePinnedUsers() for API calls.
//   • The excludeId prop should be the current user's ID (from AppContext.persona).

import { Check, ChevronDown, Search, Star, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { UserAvatar } from "@/components/ui/user-avatar";
import { cn } from "@/lib/utils";
import type { AppUser } from "@/types/user";

// ── Surname sort ──────────────────────────────────────────────────────────────

const getSurname = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1].toLowerCase();
};

const sortBySurname = (a: AppUser, b: AppUser) =>
  getSurname(a.name).localeCompare(getSurname(b.name));

// ── Pinned users hook ─────────────────────────────────────────────────────────
// Pins are per-persona so Sarah's frequent contacts don't clutter Linda's list.

const INITIAL_VISIBLE = 10;

const usePinnedUsers = (personaId: string) => {
  const storageKey = `actions-pinned-${personaId}`;

  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const togglePin = useCallback(
    (userId: string, e: React.MouseEvent) => {
      e.stopPropagation(); // don't select the user when clicking the pin
      setPinnedIds((prev) => {
        const next = prev.includes(userId)
          ? prev.filter((id) => id !== userId)
          : [...prev, userId];
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* storage full — silently ignore */
        }
        return next;
      });
    },
    [storageKey],
  );

  const isPinned = useCallback(
    (userId: string) => pinnedIds.includes(userId),
    [pinnedIds],
  );

  return { pinnedIds, togglePin, isPinned };
};

// ── UserRow ───────────────────────────────────────────────────────────────────

interface UserRowProps {
  user: AppUser;
  isSelected: boolean;
  isPinned: boolean;
  onSelect: (user: AppUser) => void;
  onTogglePin: (userId: string, e: React.MouseEvent) => void;
  showPinHint?: boolean;
  isSelf?: boolean;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  isSelected,
  isPinned,
  onSelect,
  onTogglePin,
  isSelf = false,
}) => (
  <div
    role="option"
    aria-selected={isSelected}
    className={cn(
      "group flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-md transition-colors",
      isSelected ? "bg-primary/10 text-primary" : "hover:bg-accent",
    )}
    onClick={() => onSelect(user)}
  >
    <UserAvatar name={user.name} seed={user.name} size="sm" />

    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate">
        {user.name}
        {isSelf && (
          <span className="ml-1 text-xs text-muted-foreground font-normal">
            (You)
          </span>
        )}
      </p>
      <p className="text-xs text-muted-foreground truncate">
        {user.forecasterRole}
      </p>
    </div>

    {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}

    {/* Pin button — always visible when pinned, hover-visible otherwise */}
    <button
      type="button"
      title={isPinned ? "Unpin from top of list" : "Pin to top of list"}
      className={cn(
        "flex-shrink-0 p-1 rounded transition-all",
        isPinned
          ? "text-amber-500 hover:text-amber-600"
          : "text-muted-foreground/30 hover:text-muted-foreground opacity-0 group-hover:opacity-100",
        "focus:opacity-100 focus:outline-none",
      )}
      onClick={(e) => onTogglePin(user.id, e)}
    >
      <Star className={cn("h-3.5 w-3.5", isPinned && "fill-current")} />
    </button>
  </div>
);

// ── Section label ─────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <div className="px-3 pt-2 pb-1">
    <p className="text-[10px] font-semibold tracking-wider text-muted-foreground/60">
      {label}
    </p>
  </div>
);

// ── AssigneePicker ────────────────────────────────────────────────────────────

export interface AssigneePickerProps {
  users: AppUser[];
  value: string; // selected user ID
  onChange: (userId: string) => void;
  excludeId?: string; // optional: exclude a specific user (e.g. for contexts where self-assign is not allowed)
  personaId: string; // for per-persona pin storage
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** When true, the current user (excludeId) is included in the list with a "(You)" label */
  includeSelf?: boolean;
  currentUserId?: string;
}

export const AssigneePicker: React.FC<AssigneePickerProps> = ({
  users,
  value,
  onChange,
  excludeId,
  personaId,
  placeholder = "Select person…",
  disabled = false,
  className,
  includeSelf = false,
  currentUserId,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { pinnedIds, togglePin, isPinned } = usePinnedUsers(personaId);

  // ── Derived lists ────────────────────────────────────────────────────────

  const eligible = users
    .filter((u) => (includeSelf ? true : u.id !== excludeId))
    .sort(sortBySurname);

  const selectedUser = users.find((u) => u.id === value);

  const searchResults = query.trim()
    ? eligible.filter(
        (u) =>
          u.name.toLowerCase().includes(query.toLowerCase()) ||
          u.forecasterRole.toLowerCase().includes(query.toLowerCase()),
      )
    : null;

  const pinnedUsers = eligible.filter((u) => isPinned(u.id));
  const unpinnedUsers = eligible.filter((u) => !isPinned(u.id));

  const visibleUnpinned = showAll
    ? unpinnedUsers
    : unpinnedUsers.slice(0, INITIAL_VISIBLE);
  const hiddenCount = unpinnedUsers.length - visibleUnpinned.length;

  // ── Interaction ──────────────────────────────────────────────────────────

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    setShowAll(false);
  };

  const handleSelect = (user: AppUser) => {
    onChange(user.id);
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "w-full h-9 flex items-center gap-2 px-3 text-sm rounded-md border border-input bg-background",
          "hover:bg-accent transition-colors text-left",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0",
          disabled && "opacity-50 cursor-not-allowed",
          open && "ring-2 ring-ring",
        )}
      >
        {selectedUser ? (
          <>
            <UserAvatar
              name={selectedUser.name}
              seed={selectedUser.name}
              size="xs"
            />
            <span className="flex-1 truncate font-medium">
              {selectedUser.name}
            </span>
            <span className="text-xs text-muted-foreground truncate hidden sm:block">
              {selectedUser.forecasterRole}
            </span>
            <button
              type="button"
              onClick={handleClear}
              className="ml-1 p-0.5 rounded hover:bg-muted-foreground/20 text-muted-foreground flex-shrink-0"
              title="Clear selection"
            >
              <X className="h-3 w-3" />
            </button>
          </>
        ) : (
          <>
            <span className="text-muted-foreground flex-1">{placeholder}</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform flex-shrink-0",
                open && "rotate-180",
              )}
            />
          </>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          role="listbox"
          className={cn(
            "absolute z-50 mt-1 w-full min-w-[280px] rounded-lg border border-border bg-popover shadow-lg",
            "animate-in fade-in-0 zoom-in-95",
          )}
        >
          {/* Search input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search by name or role…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowAll(false);
                }}
                onKeyDown={handleKeyDown}
                className={cn(
                  "w-full pl-8 pr-3 py-1.5 text-sm rounded-md bg-muted/50",
                  "border border-transparent focus:border-input focus:bg-background",
                  "outline-none transition-colors placeholder:text-muted-foreground",
                )}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-64 py-1 px-1">
            {/* ── Search mode ── */}
            {searchResults !== null ? (
              searchResults.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No people match "{query}"
                </div>
              ) : (
                <>
                  <SectionLabel
                    label={`${searchResults.length} result${searchResults.length !== 1 ? "s" : ""}`}
                  />
                  {searchResults.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      isSelected={u.id === value}
                      isPinned={isPinned(u.id)}
                      onSelect={handleSelect}
                      onTogglePin={togglePin}
                      isSelf={includeSelf && u.id === currentUserId}
                    />
                  ))}
                </>
              )
            ) : (
              /* ── Browse mode ── */
              <>
                {/* Pinned section */}
                {pinnedIds.length > 0 && pinnedUsers.length > 0 && (
                  <>
                    <SectionLabel label="Pinned" />
                    {pinnedUsers.map((u) => (
                      <UserRow
                        key={u.id}
                        user={u}
                        isSelected={u.id === value}
                        isPinned={true}
                        onSelect={handleSelect}
                        onTogglePin={togglePin}
                        isSelf={includeSelf && u.id === currentUserId}
                      />
                    ))}
                    {unpinnedUsers.length > 0 && (
                      <div className="my-1 border-t border-border mx-2" />
                    )}
                  </>
                )}

                {/* All people label — always just "All people" */}
                {unpinnedUsers.length > 0 && (
                  <SectionLabel label="All people" />
                )}

                {visibleUnpinned.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    isSelected={u.id === value}
                    isPinned={false}
                    onSelect={handleSelect}
                    onTogglePin={togglePin}
                    isSelf={includeSelf && u.id === currentUserId}
                  />
                ))}

                {/* Show more / show less */}
                {!showAll && hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="w-full text-xs text-muted-foreground hover:text-foreground py-2 hover:bg-accent rounded-md transition-colors"
                  >
                    Show {hiddenCount} more{" "}
                    {hiddenCount === 1 ? "person" : "people"}
                  </button>
                )}
                {showAll && unpinnedUsers.length > INITIAL_VISIBLE && (
                  <button
                    type="button"
                    onClick={() => setShowAll(false)}
                    className="w-full text-xs text-muted-foreground hover:text-foreground py-2 hover:bg-accent rounded-md transition-colors"
                  >
                    Show fewer
                  </button>
                )}

                {eligible.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No people available to assign
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer hint */}
          <div className="px-3 py-2 border-t border-border">
            <p className="text-[10px] text-muted-foreground">
              {pinnedIds.length === 0
                ? "★ Click the star next to a name to pin frequent contacts to the top"
                : `${pinnedIds.length} pinned — click ★ again to unpin`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
